/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToWishlistComponent, IWishlistActionErrorResult } from '@msdyn365-commerce/components';
import { format, ItemAvailability, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    getConfigureErrors, IBuyboxCallbacks,
    IBuyboxCommonResources,
    IBuyboxData,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps, IBuyboxShopSimilarLookViewProps,
    IBuyboxState
    //IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import {
    ArrayExtensions,
    getFullAvailableInventoryNearby,
    GetFullAvailableInventoryNearbyInput,
    IFullOrgUnitAvailability
} from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { reaction } from 'mobx';
import * as React from 'react';
import classnames from 'classnames';

import { IDlvryBuyboxProps as IBuyboxExtentionProps } from './dlvry-buybox.props.autogenerated';
import { renderAddToOrderTemplateButton } from '../../themes/dlvry-adventureworks/views/components/buybox-add-to-order-template';
import { IBuyboxViewProps } from './dlvry-buybox';
import { GetCookie, SetCookie } from '../../shared-utils/cookie-utilities';
import { hasAccessToBuy } from '../../shared-utils/attributes';
import { useBrandSchemes } from './hooks/use-brand-schemes';
import DiscountBadge from '../../components/discount-badge/discount-badge';
import { getDiscountBadge } from '../../shared-utils/badge-utils';
import QuantityInput from '../../components/common/quantity-input';
import QuantityBreakoffSelector from '../../components/common/quantity-breakoff-selector';
import { getConversionText, getNonBreakoffUOM, getTotalConversionText } from '../../shared-utils/unit-conversion-utils';
import UnitConversionDetails from '../../components/UnitConversionDetails';

export interface IBrandScheme {
    id: string;
    name?: string;
    imageUrl?: string;
}

/**
 * BuyBoxConstants enum.
 */
export enum BuyBoxConstants {
    zero = 0
}

/**
 * Render key in price function.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

/**
 * RenderFindInStore.
 * @param findInStore - FindInStore.
 * @returns -- Returns.
 */
const renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * RenderAddToOrderTemplate.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToOrderTemplate = (
    props: any,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;

    if (!props.addToOrderTemplate || !product) {
        return null;
    }

    const orderTemplateButton = renderAddToOrderTemplateButton(props, state, callbacks, product);

    return (
        <Node {...props.addToOrderTemplate.ContainerProps}>
            {props.addToOrderTemplate.errorBlock}
            {orderTemplateButton}
        </Node>
    );
};

/**
 * Add to wishlist failed function.
 * @param callbacks -Buybox callbacks.
 * @param resources -Buybox resources.
 * @param product -Simple product.
 * @returns Update error state.
 */
const onAddToWishlistFailed = (callbacks: IBuyboxCallbacks, resources: IBuyboxCommonResources, product: SimpleProduct | undefined) => (
    result: IWishlistActionErrorResult
) => {
    callbacks.updateErrorState({
        errorHost: 'WISHLIST',
        configureErrors:
            result.status === 'MISSINGDIMENSION' ? getConfigureErrors(result.missingDimensions, resources, product?.IsGiftCard) : {}
    });
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @param product - Product data.
 * @returns -- Returns.
 */
const renderAddtoWishlistButton = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks,
    product: SimpleProduct
): React.ReactNode => {
    const { context, resources } = props;
    const wishlists = props.data.wishlists.result;
    const isShowWishlitButtonText = true;
    return (
        <AddToWishlistComponent
            className='msc-add-to-cart-extra-actions'
            addToWishlistButtonText={resources.addToWishlistButtonText}
            removeFromWishlistButtonText={resources.removeFromWishlistButtonText}
            addToWishlistMessage={resources.addToWishlistMessage}
            removedFromWishlistMessage={resources.removedFromWishlistMessage}
            addItemToWishlistError={resources.addItemToWishlistError}
            removeItemFromWishlistError={resources.removeItemFromWishlistError}
            nameOfWishlist={resources.nameOfWishlist}
            data={{ product, wishlists }}
            context={context}
            ariaRole='button'
            id={props.id}
            typeName={props.typeName}
            onError={onAddToWishlistFailed(callbacks, resources, product)}
            getSelectedProduct={state.selectedProduct}
            showButtonText={isShowWishlitButtonText}
            showButtonTooltip={false}
        />
    );
};

/**
 * RenderAddToWishlist.
 * @param props - Buybox viewprops.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToWishlist = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;

    if (!props.addToWishlist || !product) {
        return null;
    }

    if (!props.config.enableWishlist) {
        return null;
    }

    const wishlistButton = renderAddtoWishlistButton(props, state, callbacks, product);
    return (
        <Node {...props.addToWishlist.ContainerProps}>
            {props.addToWishlist.errorBlock}
            {wishlistButton}
        </Node>
    );
};

/**
 * RRenderShopSimilarItem.
 * @param shopSimilarItem - ShopSimilarItem.
 * @returns -- Returns.
 */
// @ts-ignore
const renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

/**
 * RenderSocialShare.
 * @param socialShare - Social share react node array.
 * @returns -- Returns.
 */
const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

/**
 * BuyboxView.
 * @param props - Props.
 * @returns -- Returns.
 */
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const {
        context,
        ModuleProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        MediaGalleryContainerProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        addToWishlist,
        productComparisonButton,
        configure,
        description,
        findInStore,
        price,
        deposit,
        pricePer,
        additionalInformation,
        agentImporter,
        title,
        region,
        inventoryLabel,
        keyInPrice,
        callbacks,
        state,
        resources,
        app: { config: appConfig }
    } = props;

    // Track recently viewed items
    if (props.config.trackRecentlyViewedItems) {
        _updateRecentlyViewedItemsCookie(props);
    }

    const preferredStoreInfo: IStoreInfo | undefined = props.data.storeSelectorStateManager.result?.preferredStore;
    const preferredStoreId = preferredStoreInfo?.StoreId ? preferredStoreInfo.StoreId : '';
    const preferredStoreName = preferredStoreInfo?.StoreName ? preferredStoreInfo.StoreName : '';

    const [availability, setAvailability] = React.useState<string>('');
    const [isStoreChange, setIsStoreChange] = React.useState<boolean>(false);
    const [showUnitConversionDetails, setShowUnitConversionDetails] = React.useState<boolean>(false);
    const [brandSchemes] = useBrandSchemes(props.context, props.data.productSpecificationData.result);

    /**
     * GetStockInventoryLabel.
     * @param availabilityWithHours - Stock availability with hours.
     * @returns -- Returns.
     */
    const getStockInventoryLabel = (availabilityWithHours: IFullOrgUnitAvailability | undefined): string | undefined => {
        if (availabilityWithHours && ArrayExtensions.hasElements(availabilityWithHours.ProductInventoryInformation)) {
            for (const productInventoryInformation of availabilityWithHours.ProductInventoryInformation) {
                if (
                    availabilityWithHours.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId ===
                    productInventoryInformation.InventLocationId
                ) {
                    return productInventoryInformation.StockLevelLabel;
                }
            }
        }

        return undefined;
    };

    const getInventory = React.useCallback(async () => {
        const input = new GetFullAvailableInventoryNearbyInput(
            props.data.product.result?.RecordId,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            true
        );

        /**
         * IsProductInStock.
         * @param itemAvailabilities - Item availability array.
         * @returns -- Returns.
         */
        const isProductInStock = (itemAvailabilities: ItemAvailability[] | undefined): boolean => {
            if (!appConfig.enableStockCheck) {
                return true;
            }

            if (ArrayExtensions.hasElements(itemAvailabilities)) {
                return itemAvailabilities[BuyBoxConstants.zero].AvailableQuantity! > BuyBoxConstants.zero;
            }

            return false;
        };

        await getFullAvailableInventoryNearby(input, props.context.actionContext)
            .catch((error: Error) => {
                props.telemetry.error(error.message);
            })
            .then(response => {
                if (response && ArrayExtensions.hasElements(response)) {
                    const isPreferredStore = (value: IFullOrgUnitAvailability) =>
                        value.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === preferredStoreId;
                    const filteredFullOrgUnitAvailability = response.filter(isPreferredStore);
                    const preferredStoreAvailability = ArrayExtensions.hasElements(filteredFullOrgUnitAvailability)
                        ? filteredFullOrgUnitAvailability[BuyBoxConstants.zero]
                        : undefined;
                    const stockStatusLabel: string | undefined = getStockInventoryLabel(preferredStoreAvailability);
                    const isInStock = isProductInStock(preferredStoreAvailability?.OrgUnitAvailability?.ItemAvailabilities);
                    const resourceLabel = isInStock ? props.resources.inStockText : props.resources.outOfStockText;
                    const stockText: string | undefined = stockStatusLabel ? stockStatusLabel : resourceLabel;
                    const preferredStoreAvailabilityText = `${format(
                        props.resources.availabilityAtPreferredStoreText,
                        preferredStoreName
                    )} ${stockText}`;
                    setAvailability(preferredStoreAvailabilityText);
                }
            });

        setIsStoreChange(false);
    }, [
        props.data.product.result?.RecordId,
        appConfig.enableStockCheck,
        preferredStoreId,
        preferredStoreName,
        props.context.actionContext,
        props.resources.availabilityAtPreferredStoreText,
        props.resources.inStockText,
        props.resources.outOfStockText,
        props.telemetry
    ]);

    React.useEffect(() => {
        if (props.config.displayStockAvailability) {
            getInventory().catch((error: Error) => {
                props.telemetry.error(error.message);
            });
        }
    }, [getInventory, isStoreChange, props.telemetry, props.config.displayStockAvailability]);

    // Mirror button text to title of Microsoft buttons
    React.useEffect(() => {
        const setTitleToTextContent = (element: HTMLElement | null) => {
            if (element) {
                element.title = element?.textContent || '';
            }
        };

        setTitleToTextContent(document.querySelector('.msc-add-to-wishlist.msc-add-to-cart-extra-actions'));
        setTitleToTextContent(document.querySelector('.msc-add-to-cart-extra-actions.msc-add-to-order-template-icon'));
    }, [props.data.wishlists.result, props.data.product.result, props.data.orderTemplates.result]);

    reaction(
        () => props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
        () => {
            setIsStoreChange(true);
        }
    );

    /**
     * RenderAvailabiltyAtPreferredStore.
     * @returns -- Returns.
     */
    const renderAvailabilityAtPreferredStore = (): JSX.Element | undefined => {
        const hasProductDimensions = ArrayExtensions.hasElements(props.data.product.result?.Dimensions);
        if (hasProductDimensions && props.data.product.result?.MasterProductId === undefined) {
            return undefined;
        }
        const containerProps: INodeProps = {
            className: 'ms-buybox__preferred-store-availability',
            tag: 'span'
        };

        return <Node {...containerProps}>{availability}</Node>;
    };

    const isAuthenticated = props.context.request.user.isAuthenticated;
    const hasAccess = hasAccessToBuy(context, props.data.organizationInformation.result, props.data.productSpecificationData.result);
    const conversionText = getConversionText(
        context,
        props.data.product.result?.DefaultUnitOfMeasure,
        state.unitConversions || [],
        props.data.product.result,
        state.breakoff
    );
    const totalConversionText = getTotalConversionText(
        context,
        props.data.product.result,
        state.unitConversions,
        state.quantity,
        state.breakoff
    );
    const unitConversionDetailsEnabled = context.app.config?.enableUnitConversionDetails;

    return (
        <Module {...ModuleProps}>
            <Node className='ms-buybox__product-top'>
                <Node style={{ position: 'relative' }} {...MediaGalleryContainerProps}>
                    {props.mediaGallery}
                    <DiscountBadge
                        context={props.context}
                        badge={getDiscountBadge(props.data.productPrice.result, props.data.product.result?.FallbackBadge)}
                        size='large' />
                </Node>
                <Node {...ProductInfoContainerProps}>
                    <div className='media-gallery-actions'>
                        {isAuthenticated && addToWishlist && renderAddToWishlist(props, state, callbacks)}
                        {isAuthenticated && addToOrderTemplate && renderAddToOrderTemplate(props, state, callbacks)}
                        {isAuthenticated && productComparisonButton}
                    </div>
                    {title}
                    <div
                        className={classnames('ms-buybox__product-conversion', { enabled: unitConversionDetailsEnabled })}
                        style={{ visibility: conversionText ? undefined : 'hidden' }}
                        onClick={() => setShowUnitConversionDetails(!showUnitConversionDetails)}
                    >
                        {conversionText || '.'}
                        <UnitConversionDetails
                            context={context}
                            enabled={unitConversionDetailsEnabled}
                            isOpen={showUnitConversionDetails}
                            toggle={() => setShowUnitConversionDetails(!showUnitConversionDetails)}
                            productName={props.data.product.result?.Name}
                            productUOM={props.data.product.result?.DefaultUnitOfMeasure}
                            unitConversions={props.state.unitConversions || []}
                            simpleProduct={props.data.product.result}
                            breakoff={props.state.breakoff}
                        />
                    </div>
                    {props.data.product.result?.ItemId && (
                        <div className='ms-buybox__product-item-id'>
                            {props.data.product.result?.ItemId}
                        </div>
                    )}
                    {additionalInformation}
                    {agentImporter}
                    {region}
                    {description && <Node className='ms-buybox__description-section'>
                        {description}
                    </Node>}
                    {isAuthenticated && price && hasAccess && (
                        <Node className='msc-buybox__price-section'>
                            {price}
                            {price && hasAccess && (
                                <Node className='msc-buybox__price-section__extra'>
                                    {pricePer}
                                    {deposit}
                                </Node>
                            )}
                            {state.firstExpiringDiscount && (
                                <div className='msc-buybox__price-section__expiring'>
                                    {format(resources.expiringDiscountText, context.cultureFormatter.formatDate(state.firstExpiringDiscount, {
                                        day: '2-digit',
                                        month: 'long',
                                        year: state.firstExpiringDiscount.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                                    }))}
                                </div>
                            )}
                        </Node>
                    )}
                    {isAuthenticated && hasAccess && (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <QuantityBreakoffSelector
                                context={props.context}
                                product={props.data.product.result}
                                quantity={state.quantity}
                                breakoff={state.breakoff}
                                unitConversions={state.unitConversions}
                                onQuantityChange={callbacks.updateQuantity}
                                onBreakoffChange={props.onBreakoffChange}
                            />
                            <QuantityInput
                                context={props.context}
                                product={props.data.product.result}
                                quantity={state.quantity}
                                breakoff={state.breakoff}
                                onQuantityChange={callbacks.updateQuantity}
                                nonBreakoffUOM={getNonBreakoffUOM(props.data.product.result, props.state.unitConversions)}
                            />
                            {totalConversionText && (
                                <span className='msc-buybox__total-conversion-text'>
                                    =
                                </span>
                            )}
                            <span className='msc-buybox__total-conversion-text'>
                                {totalConversionText}
                            </span>
                        </div>
                    )}
                    {isAuthenticated && hasAccess && (
                        <Node {...addToCart.ContainerProps}>
                            {addToCart.button}
                        </Node>
                    )}
                    {inventoryLabel}
                    {configure && renderConfigure(configure)}
                    {keyInPrice && renderKeyInPrice(keyInPrice)}
                    {isAuthenticated && !hasAccess && <div className='msc-buybox__missing-license'>{resources.missingLicenseText}</div>}
                    {isAuthenticated && props.config.displayStockAvailability && preferredStoreId && renderAvailabilityAtPreferredStore()}
                    {isAuthenticated && findInStore && renderFindInStore(findInStore)}
                    {isAuthenticated && findInStore?.productPickupOptionList ? (
                        <div className='msc-buybox__pickup-options'>{findInStore.productPickupOptionList}</div>
                    ) : null}
                    {_renderSocialShare(props.slots && props.slots.socialShare)}
                    {brandSchemes && brandSchemes.length > 0 && (
                        <div className='msc-buybox__brand-schemes'>
                            {brandSchemes.map((scheme, index) => (
                                <div key={index} className='msc-buybox__brand-schemes__item'>
                                    <div className='msc-buybox__brand-schemes__img'>
                                        {scheme.imageUrl
                                            ? <img src={scheme.imageUrl} alt={scheme.name} title={scheme.name} />
                                            : <div className='missing-icon' title={`Couldn't find full text and image for "${scheme.id}"`}>
                                                <i className='fa-solid fa-square-question' />
                                                {scheme.id}
                                            </div>
                                        }
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Node>
            </Node>
            {ArrayExtensions.hasElements(props.slots.alternativeProductsCarousel) ? (
                <Node className='msc-buybox__alternative-products-carousel'>
                    {props.slots.alternativeProductsCarousel[0]}
                </Node>
            ) : null}
            {ArrayExtensions.hasElements(props.slots.productSpecification) ? (
                <Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
            ) : null}
        </Module>
    );
};

/**
 * Keep track of recently viewed items.
 * @param props
 */
const _updateRecentlyViewedItemsCookie = (props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>): void => {
    const cookieName = '_i365___recently_viewed_products';
    const cookieAge: number = 31536000000;
    const cookieValue = GetCookie(props.context.request.cookies, cookieName);
    let productIds = (cookieValue && cookieValue.split(',').map(x => +x)) || [];
    const recordId: number = (props.data.product.result && props.data.product.result.RecordId) || 0;
    const max: number = props.config.maxRecentlyViewedItemsCount || 10;
    if (recordId !== 0 && productIds.indexOf(recordId) === -1) {
        productIds.push(recordId);
    }
    if (productIds.length > max) {
        productIds = productIds.slice(Math.max(productIds.length - max, 0));
    }
    const newCookieValue = String(productIds);
    SetCookie(props.context.request.cookies, newCookieValue, cookieName, cookieAge);
};
export default BuyboxView;
